import { EditOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import type { DataGridProProps, GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { StyleObj } from '../../../@types';
import { CRMUserData, Customer } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import countries from '../../../constants/countries.json';
import { useCRMUser } from '../../../contexts/CRMUserContext';
import { useModal } from '../../../contexts/ModalContext';
import { defaultColumnsCustomers } from '../../../helpers/table';
import useMutateData from '../../../hooks/useMutateData';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import FormModal from '../../modals/FormModal';
import CRMDetailsDrawer from '../../organisms/crm/CRMDetailsDrawer';
import TableTemplate from '../../templates/TableTemplate';
import { CUSTOMER_TAB_CONFIG } from '../../../config/crm';
import WalletIcon from '../../icons/WalletIcon';
import { useNavigate, useSearchParams } from 'react-router';
import { dayjs } from '../../../config/dayjs';
import { createIsActiveColumn } from '../../../helpers/columns';
import Impersonate from '../../icons/Impersonate';
import { useMemo, useState } from 'react';
import ConfirmationModal from '../../modals/ConfirmationModal';
import usePermissions from '../../../hooks/usePermissions';
import { useMarketplace } from '../../../hooks/useMarketplace';
import { runtimeEnv } from '../../../config/runtimeEnv';
import { playerFilterParams } from '../../organisms/forms/playerFilterParams';

const styles: StyleObj = {
  table: {
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
};

const CustomersPage = () => {
  const [searchParams] = useSearchParams();
  const filterQuery = useMemo(
    () => playerFilterParams.toApiQuery(playerFilterParams.parse(searchParams)),
    [searchParams]
  );

  const {
    data: customerData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<Customer>('customer/search', undefined, undefined, 'crm', undefined, filterQuery);
  const { updateData } = useMutateData('customer', [QUERY_KEYS.customers], 'crm');
  const { handleSort } = useSort(changeQuery, 'crm');
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { config: marketPlaceConfig } = useMarketplace();

  const canImpersonatePlayer = hasPermission('impersonatePlayer');
  const canUpdateStatus = hasPermission('updateCustomerStatus');
  const canEditCustomer = hasPermission('editAssignedCustomers');
  const canCreateCustomer = hasPermission('createPlayer');
  const createItemBtnLabel = canCreateCustomer ? 'Add new player' : undefined;

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const openEditDialog = (row: Customer) => {
    openModal(<FormModal form='customers' />, row);
  };

  const [currentUserId, setCurrentUserId] = useState<string>('');
  const { createData: impersonatePlayer } = useMutateData(
    `admin/realms/sportsbook-end-users/users/${currentUserId}/impersonation`,
    [QUERY_KEYS.customerImpersonate, currentUserId],
    'kc'
  );

  const handleImpersonate = (id: string) => {
    const clientUrl = runtimeEnv.sportsbookClientUrl ?? `https://${marketPlaceConfig?.domain}`;
    setCurrentUserId(id);
    impersonatePlayer({ userId: id }, undefined, undefined, () => window.open(clientUrl, '_blank'));
  };

  const handleConfirmModal = (id: string) => {
    openModal(
      <ConfirmationModal
        title='Are you sure you want to Impersonate this player?'
        actionButtonLabel='Impersonate'
        handleConfirm={() => handleImpersonate(id)}
        actionButtonClose='Cancel'
      />
    );
  };

  const columns: GridColDef[] = [
    createColumn('firstName', 'First Name'),
    createColumn('lastName', 'Last Name'),
    createColumn('username', 'Username', {
      alwaysVisible: true,
      minWidth: 150,
    }),
    createColumn('country', 'Country', {
      minWidth: 200,
      valueGetter: (value, row, column, apiRef) => {
        const country = countries.find((c) => c.cca2 === value);
        return country ? country.name : value;
      },
    }),
    createColumn('phone', 'Phone Number', { minWidth: 150 }),
    createColumn('email', 'Email', { flex: 1.5, minWidth: 150 }),
    createColumn('createdAt', 'Date Created', {
      alwaysVisible: true,
      valueGetter: (value, row, column, apiRef) => dayjs(row.createdAt).format('DD.MM.YYYY'),
      flex: 0.75,
    }),
    createColumn('lastOnline', 'Last Online', {
      valueGetter: (value, row, column, apiRef) => row.lastOnline && dayjs(row.lastOnline).fromNow(),
      flex: 0.75,
    }),
    createColumn('address', 'Address', { minWidth: 200 }),
    createColumn('zip', 'Zip Code', {
      flex: 0.75,
    }),
    createColumn('city', 'City', {}),
    createColumn('region', 'Region', {}),
    createColumn('language', 'Language', {}),
    createColumn('dateOfBirth', 'Date of Birth', {
      valueGetter: (value, row, column, apiRef) => row.dateOfBirth && dayjs(row.dateOfBirth).format('DD.MM.YYYY'),
      flex: 0.75,
    }),
    createColumn('gender', 'Gender', {}),
    createColumn('parent', 'Parent', {
      sortable: false,
      valueGetter: (value, row, column, apiRef) =>
        row.agent && (row.agent?.fullName || `${row.agent?.firstName} ${row.agent?.lastName}`),
    }),
    createColumn('affiliateCampaign', 'Campaign', {
      sortable: false,
      valueGetter: (value, row, column, apiRef) => row.affiliateCampaign?.name,
    }),
    createColumn('createdBy', 'Created By'),
    createColumn('nationalId', 'National ID', {}),
    createColumn('currency', 'Currency', {}),
    createIsActiveColumn<Customer>({
      updateData,
      disabled: !canUpdateStatus,
    }),
    createColumn('edit', 'Actions', {
      sortable: false,
      disableExport: true,
      renderCell: (params: GridCellParams<Customer>) => (
        <>
          <IconButton
            onClick={() => navigate(`/crm/players/${params.row.userId}/wallet/total-credit`)}
            disabled={!params.row.userId}
          >
            <WalletIcon />
          </IconButton>
          {canEditCustomer && (
            <IconButton onClick={() => openEditDialog(params.row)} color='primary'>
              <EditOutlined />
            </IconButton>
          )}
          {canImpersonatePlayer && (
            <IconButton onClick={() => handleConfirmModal(params.row.userId)}>
              <Impersonate />
            </IconButton>
          )}
        </>
      ),
    }),
  ];

  const { setSelectedCRMUser } = useCRMUser();

  const handleCellClick = (params: GridCellParams<Customer>) => {
    if (params.field !== 'edit' && params.field !== 'isActive') {
      setSelectedCRMUser(params.row as CRMUserData);
    }
  };

  return (
    <>
      <TableTemplate
        rows={customerData?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={customerData?.count ?? 0}
        createItemBtnLabel={createItemBtnLabel}
        defaultVisibleColumns={defaultColumnsCustomers}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        onCellClick={handleCellClick}
        formType='customers'
        sx={styles.table as DataGridProProps['sx']}
        changeQuery={changeQuery}
        showExport
      />
      <CRMDetailsDrawer config={CUSTOMER_TAB_CONFIG} />
    </>
  );
};

export default CustomersPage;
